import React, { Fragment } from "react";
import clsx from "clsx";
import { useLocation, Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { useAppSelector } from "../../../app/hooks";
import { ScrollBar } from "@ht/components";
import Icon from "../../../components/icon";
import { Menu } from "../../../app/auth/types";

interface SidebarItemProps {
    navigation: Menu;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ navigation }) => {
    const { url, label, submenu, icon } = navigation;

    const location = useLocation();
    const active =
        url === "/" ? location.pathname === url : location.pathname == (url as string);

    return submenu &&
        submenu?.filter(
            (sub) => sub.position === "BOTH" || sub.position === "SIDEBAR"
        ).length > 0 ? (
        <ul className="px-2.5 space-y-2">
            <li className="px-2.5 text-gray-700 font-bold">{label}</li>
            {submenu.map((sub) => (
                <li
                    key={sub.id}
                >
                    <Link to={sub.url as string} className={clsx(
                        "flex px-2.5 py-2 gap-1.5 rounded-md font-semibold text-gray-700 hover:bg-gray-200",
                        {
                            "bg-primary-100 text-primary":
                                location.pathname == (sub.url as string),
                        }
                    )}>
                        <Icon name={sub.icon as string} className="w-4 h-4" />

                        <span
                            className={twMerge(
                                clsx("text-gray-700", {
                                    "text-primary": active,
                                })
                            )}
                        >
                            {sub.label}
                        </span>
                    </Link>
                </li>
            ))}
        </ul>
    ) : (
        <ul>
            <li
                className={clsx(
                    "flex gap-1.5 px-2.5 py-2 my-1 rounded-md text-gray-700 hover:bg-gray-200",
                    { "bg-primary-100 text-primary": active }
                )}
            >
                <Icon name={icon as string} className="w-4 h-4" />
                <Link to={url as string}>
                    <span
                        className={twMerge(
                            clsx("text-gray-700", { "text-primary": active })
                        )}
                    >
                        {label}
                    </span>
                </Link>
            </li>
        </ul>
    );
};

const Sidebar = () => {
    const { navigations } = useAppSelector((state) => state.auth);

    const sidebarNav = React.useMemo(() => {
        return navigations.filter(
            (x) => x.position === "SIDEBAR" || x.position === "BOTH"
        );
    }, [navigations]);

    return (
        <nav
            className={clsx(
                "hidden py-4 h-full bg-gray-100 lg:block lg:w-[270px]"
            )}
        >
            <ScrollBar>
                {sidebarNav.map((nav) => {
                    return (
                        <div key={nav.id}>
                            <div className="px-5 mb-5 text-lg text-gray-700 font-bold">
                                {nav.label}
                            </div>
                            <div className="space-y-5">
                                {nav.submenu?.map((sub, idx) => (
                                    <Fragment key={sub.id}>
                                        <div
                                            className={clsx("px-5", {
                                                hidden: idx <= 0,
                                            })}
                                        >
                                            <div className="h-[1px] bg-gray-200"></div>
                                        </div>
                                        <SidebarItem
                                            key={sub.id}
                                            navigation={sub}
                                        />
                                    </Fragment>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </ScrollBar>
        </nav>
    );
};

export default Sidebar;
